/* eslint-disable react/default-props-match-prop-types,react/require-default-props */
// See https://github.com/facebook/react/issues/4494
import moment from 'moment';
import React from 'react';
import {
  func,
  shape,
  string,
} from 'prop-types';
import _ from 'lodash';
import set from 'lodash.set';
import { applicationFormTrack } from 'components/tracking/TrackingUtils';
import { setFieldState, removeParenthesis } from '../../Forms/utils/FormUtil';

class BaseApplicationForm extends React.Component {
  static initState = (props) => {
    const { form } = props;
    const { steps } = form;
    const formState = {};
    Object.entries(steps).forEach((step) => {
      formState[step[0]] = {};
      if (step[1].fields) {
        Object.entries(step[1].fields).forEach((field) => {
          formState[step[0]][field[0]] = {
            value: field[1].defaultValue || '',
            isValid: field[1].defaultValue ? true : !field[1].required,
            message: '',
            validation: {},
            isRequired: field[1].required,
            mapping: field[1].mapping,
          };
        });
      }
    });
    formState.selectedIndex = 0;
    return formState;
  };

  FINAL_STATE = 'finalFormState';

  constructor(props) {
    super(props);
    this.handleMutateResponse = this.handleMutateResponse.bind(this);
    this.handleFinalMutateResponse = this.handleFinalMutateResponse.bind(this);
    this.setDefaultValueForStep = this.setDefaultValueForStep.bind(this);
    this.moveToNextPage = this.moveToNextPage.bind(this);
    this.progressIfValid = this.progressIfValid.bind(this);

    this.state = {
      selectedIndex: 0,
      formState: null,
      awaitingMutationResponse: false,
      globalClean: true,
    };
  }

  componentDidUpdate() {
    const { awaitingMutationResponse, next } = this.state;
    const { mutateResult } = this.props;
    if (mutateResult instanceof Error) {
      throw new Error(mutateResult.message);
    }
    if (mutateResult && awaitingMutationResponse && next === this.FINAL_STATE) {
      this.handleFinalMutateResponse();
    } else if (mutateResult && awaitingMutationResponse) {
      this.handleMutateResponse();
    }
  }

  setDefaultValueForStep = (stepName) => {
    const { form: { steps } } = this.props;
    const { formState } = this.state;
    Object.entries(steps).forEach((step) => {
      if (step[1].fields && step[0] === stepName) {
        Object.entries(step[1].fields).forEach((field) => {
          if (field[1].mapping && field[1].mapping.defaultValue != null && formState[step[0]][field[0]].value === '') {
            formState[step[0]][field[0]].value = field[1].mapping.defaultValue;
          }
        });
      }
    });
    this.setState({ formState });
  };

  handleMutateResponse = () => {
    const { mutateResultComplete } = this.props;
    if (mutateResultComplete) {
      mutateResultComplete();
    }
    const { selectedIndex, next } = this.state;
    this.setState({ awaitingMutationResponse: false });
    setTimeout(() => {
      this.moveToNextPage(selectedIndex, next);
    }, 10);
  };

  moveToNextPage = (selectedIndex, next) => {
    let i = selectedIndex;
    if (next && selectedIndex < this.pageIDList.length - 1) {
      i++;
    } else if (!next && i > 0) {
      i--;
    }
    this.onStepClickWithIndex(this.pageIDList[i], i);
  };

  handleFinalMutateResponse = () => {
    const {
      mutateResultComplete,
      history,
      success,
      applicationToken,
    } = this.props;
    if (mutateResultComplete) {
      mutateResultComplete();
    }
    const token = encodeURIComponent(applicationToken);
    this.setState({ awaitingMutationResponse: false });
    const resultsPagePathTemplate = _.template(success.resultsPagePath);
    const path = resultsPagePathTemplate({ transactionNumber: token });
    history.push(path);
    /* Tracking */
    applicationFormTrack(this.props, { pageId: 'applicationComplete' });
  };

  getDateDifference = (start, end) => moment(end).diff(start, 'days') + 1;

  formatDate = birthday => moment(birthday).format('DD-MM-YYYY');

  onStepProgressClick = (next) => {
    const { selectedIndex } = this.state;
    if (next === false) {
      this.moveToNextPage(selectedIndex, next);
      return;
    }
    this.setState({ next, awaitingMutationResponse: true });
    const {
      mutate,
      language,
      productId,
      applicationToken,
      criteria,
      form,
      localisation,
    } = this.props;
    const { formVersionTag } = form;
    const { pageId } = this.state;
    this.setDefaultValueForStep(pageId);
    const { formState } = this.state;
    const formStageTag = next === this.FINAL_STATE ? 'ApplicationComplete' : pageId;
    const variables = {
      language: localisation[language],
      input: this.formatMutationQuery(formState, productId, criteria),
      formVersionTag,
      formStageTag,
      applicationToken,
    };
    /* Tracking */
    applicationFormTrack(this.props, this.state);
    if (mutate) {
      mutate({ variables })
        .catch((e) => {
          this.setState(() => { throw e; });
        });
    }
  };

  onSubmitFinalForm = () => {
    this.progressIfValid(this.FINAL_STATE);
  };

  progressIfValid = (next) => {
    const { form } = this.props;
    const { selectedIndex, globalClean } = this.state;
    const pageId = this.pageIDList[selectedIndex];
    const currentFormState = this.getState(pageId);
    if (this.isValidForm(currentFormState)) {
      this.onStepProgressClick(next);
      return true;
    }
    this.updateFormValidation(pageId, Object.entries(currentFormState).map(kvp => ({
      name: kvp[0],
      value: kvp[1].value,
      validation: form.steps[pageId].fields[kvp[0]].validation,
      mapping: form.steps[pageId].fields[kvp[0]].mapping,
      isRequired: form.steps[pageId].fields[kvp[0]].required,
      isValid: form.steps[pageId].fields[kvp[0]].isValid,
      globalClean,
    })));
    if (document.querySelectorAll('.input-wrapper.error input')[0]) {
      document.querySelectorAll('.input-wrapper.error input')[0].focus();
    }
    return false;
  }

  // This is used to be a simpler generic progression if form is valid.
  // Simple forms without dynamic validation rules can call this to progress if form is valid.
  onStepProgressClickIfValid = () => {
    this.progressIfValid(true);
  };

  setField = (page, field, value, validation, callback) => {
    const { formState } = this.state;
    this.setState({
      formState: {
        ...formState,
        [page]: {
          ...formState[page],
          [field]: setFieldState({
            value,
            validation,
            formState: formState[page],
            isRequired: _.get(formState[page][field], 'isRequired'),
            mapping: _.get(formState[page][field], 'mapping'),
          }),
        },
      },
    }, callback);
  };

  validateField = (page, field, value, validation) => {
    const { formState } = this.state;
    return setFieldState({
      value,
      validation,
      formState: formState[page],
      isRequired: _.get(formState[page][field], 'isRequired'),
      mapping: _.get(formState[page][field], 'mapping'),
    })?.message;
  };

  isValidForm = (state) => {
    let valid = true;
    Object.values(state).forEach((field) => {
      if (field.isRequired === true && (field.value === null || field.value === '' || Number.isNaN(field.value))) {
        valid = valid && false;
      } else if (field.isValid === false) {
        valid = valid && false;
      }
    });
    return valid;
  };

    goToPageId = (pageId) => {
      this.pageIDList.forEach((page, index) => {
        if (pageId === page) {
          this.onStepClickWithIndex(page, index);
        }
      });
    };

  updateFormValidation = (page, fields) => {
    const { formState } = this.state;
    const form = {
      ...formState,
      [page]: {
        ...formState[page],
      },
    };
    fields.forEach((field) => {
      form[page][field.name] = setFieldState({
        value: field.value,
        validation: field.validation,
        formState: formState[page],
        isRequired: field.isRequired,
        mapping: field.mapping,
      });
    });
    this.setState({
      formState: form,
      globalClean: false,
    });
  };

    onStepClickWithIndex = (pageId, i, sectionName) => {
      const { onChange } = this.props;
      this.setState({
        selectedIndex: i,
        // eslint-disable-next-line react/no-unused-state
        pageId,
        globalClean: true,
        sectionName,
      });
      if (typeof window !== 'undefined') window.scrollTo(0, 0);
      if (onChange) onChange(i);
    };

    formatMutationQuery(formState, productId, criteria) {
      const fieldArrays = [];
      _.forEach(_.values(formState), (page) => {
        _.forEach(_.values(page), (field) => {
          fieldArrays.push(field);
        });
      });
      const query = {};
      if (criteria) {
        query.loanDetails = criteria;
      }
      fieldArrays.forEach((field) => {
        if (field.isValid && field.mapping) {
          if (field.mapping.mutationObjectPaths) {
            let { value } = field;
            if (field.mapping.behaviour === 'reverseBoolean' && typeof value === 'boolean') {
              value = !value;
            } else if (field.mapping.behaviour === 'removeParenthesis' && typeof value === 'string') {
              value = removeParenthesis(value);
            }
            if (value != null && value !== '') {
              field.mapping.mutationObjectPaths.forEach(path => set(query, path, value));
              if (field.mapping.additionalFields) {
                field.mapping.additionalFields.forEach(af => set(query, af.path, af.value));
              }
            }
          }
        }
      });
      if (productId) {
        query.productId = productId;
      }
      return query;
    }
}

export default BaseApplicationForm;

BaseApplicationForm.defaultProps = {
  onChange: null,
  criteria: null,
  // Will provide default props for these because they are passed in from
  // the 'React.cloneElement(children; call in FunnelPageQuery. See
  // https://github.com/facebook/react/issues/4494 for details
  applicationToken: '',
  history: {},
};

BaseApplicationForm.propTypes = {
  criteria: shape({}),
  mutate: func,
  mutateResult: shape({}),
  form: shape({}).isRequired,
  mutateResultComplete: func,
  onChange: func,
  productId: string.isRequired,
  applicationToken: string.isRequired,
  language: string.isRequired,
  localisation: shape({}).isRequired,
  history: shape({
    push: func,
  }).isRequired,
  success: shape({
    resultsPagePath: string.isRequired,
  }).isRequired,
};
